import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { CalendarIcon, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getDistricts, createLead } from "@/lib/api";
import { cn } from "@/lib/utils";

const inputCls =
  "bg-white text-[#141517] border-[rgba(20,21,23,0.16)] placeholder:text-[#9a9ea5] focus-visible:ring-[#C8A24A]";

export const LeadForm = ({ presetDistrict = "", presetPackage = "", variant = "light" }) => {
  const { data: districts = [] } = useQuery({ queryKey: ["districts"], queryFn: getDistricts });
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [date, setDate] = useState(null);
  const [district, setDistrict] = useState(presetDistrict || "");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) return toast.error("Lütfen adınızı girin.");
    if (phone.trim().length < 6) return toast.error("Lütfen geçerli bir telefon girin.");
    setLoading(true);
    try {
      await createLead({
        name: name.trim(),
        phone: phone.trim(),
        wedding_date: date ? format(date, "yyyy-MM-dd") : null,
        district: district || null,
        package: presetPackage || null,
        message: message.trim() || null,
      });
      setDone(true);
      toast.success("Talebiniz alındı! En kısa sürede dönüş yapacağız.");
    } catch (err) {
      toast.error("Bir hata oluştu. Lütfen WhatsApp'tan ulaşın.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-10" data-testid="lead-form-success">
        <CheckCircle2 className="w-12 h-12 mx-auto text-[#3F6B5B]" />
        <h3 className="font-display text-2xl mt-4 text-[#141517]">Teşekkürler!</h3>
        <p className="text-[#5B5F66] mt-2">
          Talebiniz bize ulaştı. En kısa sürede sizinle iletişime geçeceğiz.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="grid gap-4" data-testid="lead-form">
      <div className="grid gap-2">
        <Label htmlFor="lf-name" className="text-[#141517]">Ad Soyad</Label>
        <Input id="lf-name" data-testid="lead-form-name-input" className={inputCls}
          value={name} onChange={(e) => setName(e.target.value)} placeholder="Adınız Soyadınız" />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="lf-phone" className="text-[#141517]">Telefon</Label>
        <Input id="lf-phone" type="tel" data-testid="lead-form-phone-input" className={inputCls}
          value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="05xx xxx xx xx" />
      </div>
      <div className="grid gap-2">
        <Label className="text-[#141517]">Düğün / Çekim Tarihi</Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button type="button" variant="outline" data-testid="lead-form-date-input"
              className={cn("justify-start font-normal", inputCls, !date && "text-[#9a9ea5]")}>
              <CalendarIcon className="mr-2 h-4 w-4" />
              {date ? format(date, "d MMMM yyyy", { locale: tr }) : "Tarih seçin (opsiyonel)"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar mode="single" selected={date} onSelect={setDate} initialFocus locale={tr} />
          </PopoverContent>
        </Popover>
      </div>
      <div className="grid gap-2">
        <Label className="text-[#141517]">İlçe</Label>
        <Select value={district} onValueChange={setDistrict}>
          <SelectTrigger data-testid="lead-form-district-select" className={inputCls}>
            <SelectValue placeholder="İlçe seçin (opsiyonel)" />
          </SelectTrigger>
          <SelectContent className="max-h-64">
            {districts.map((d) => (
              <SelectItem key={d.slug} value={d.slug}>{d.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid gap-2">
        <Label htmlFor="lf-msg" className="text-[#141517]">Mesajınız</Label>
        <Textarea id="lf-msg" data-testid="lead-form-message-input" className={inputCls}
          value={message} onChange={(e) => setMessage(e.target.value)}
          placeholder="Konsept, paket veya sorularınız..." rows={3} />
      </div>
      <Button type="submit" size="lg" disabled={loading} data-testid="lead-form-submit-button"
        className="bg-[#C8A24A] text-[#141517] hover:bg-[#D7B86A] font-semibold">
        {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
        Uygunluk Sor · Teklif Al
      </Button>
      <p className="text-xs text-[#5B5F66] text-center">
        Göndererek sizinle iletişime geçmemize izin vermiş olursunuz. Spam yok.
      </p>
    </form>
  );
};

export default LeadForm;
